<template>
  <div class="search-panel" v-show="visible">
    <div class="search-header">
      <span class="search-title">文内搜索和替换</span>
      <i class="el-icon-close search-close" @click="handleClose"></i>
    </div>
    
    <div class="search-container">
      <div class="search-row">
        <el-input
          v-model="searchText"
          placeholder="请输入搜索内容"
          @keyup.enter.native="search"
          clearable
          size="small"
          ref="searchInput"
        >
          <el-button slot="append" icon="el-icon-search" @click="search" size="small">搜索</el-button>
        </el-input>
      </div>
      
      <div class="search-row">
        <el-input
          v-model="replaceText"
          placeholder="请输入替换内容"
          clearable
          size="small"
        ></el-input>
      </div>
      
      <div class="search-row replace-buttons">
        <el-button @click="replace" :disabled="!hasMatches" size="small">替换</el-button>
        <el-button @click="replaceAll" :disabled="!hasMatches" size="small">全部替换</el-button>
      </div>
      
      <div class="search-row delete-buttons">
        <el-button @click="deleteCurrent" :disabled="!hasMatches" size="small" type="danger" plain>消除</el-button>
        <el-button @click="deleteAll" :disabled="!hasMatches" size="small" type="danger" plain>全部消除</el-button>
      </div>
      
      <div class="search-row space-buttons">
        <el-button @click="searchSpaces" size="small" type="primary" plain>搜索空格</el-button>
        <el-button @click="clearAllSpaces" size="small" type="danger" plain>一键清除空格</el-button>
      </div>
      
      <div class="search-options">
        <el-checkbox v-model="caseSensitive" size="small">区分大小写</el-checkbox>
        <div class="match-info" v-if="hasMatches">
          {{ currentMatchIndex + 1 }}/{{ matches.length }}
        </div>
      </div>
      
      <div class="search-navigation" v-if="hasMatches">
        <el-button 
          size="mini" 
          icon="el-icon-arrow-up" 
          @click="previousMatch"
          :disabled="matches.length <= 1"
          title="上一个匹配项"
        ></el-button>
        <el-button 
          size="mini" 
          icon="el-icon-arrow-down" 
          @click="nextMatch"
          :disabled="matches.length <= 1"
          title="下一个匹配项"
        ></el-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SearchPanel',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    editor: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      searchText: '',
      replaceText: '',
      caseSensitive: false,
      matches: [],
      currentMatchIndex: 0,
      highlightedElements: [],
      editorUpdateHandler: null,
      isHighlighting: false,
      retryCount: 0,
      domObserver: null
    }
  },
  computed: {
    hasMatches() {
      return this.matches.length > 0
    },
    editorInstance() {
      return this.editor || (this.$parent.$refs.editor && this.$parent.$refs.editor.editor)
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.clearSearch()
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus()
          }
        })
      } else {
        this.clearHighlight()
      }
    },
    searchText() {
      this.forceRemoveAllHighlights()
      this.clearSearch()
    },
    caseSensitive() {
      if (this.searchText) {
        this.search()
      }
    }
  },
  methods: {
    search() {
      this.isHighlighting = false
      this.retryCount = 0
      this.forceRemoveAllHighlights()
      this.clearSearch()
      
      if (this.searchText.length === 0) {
        return
      }
      
      if (!this.editorInstance || !this.editorInstance.view) {
        return
      }
      
      try {
        const text = this.getEditorText(this.editorInstance)
        this.matches = this.findMatches(text)
        
        if (this.hasMatches) {
          this.currentMatchIndex = 0
          this.highlightAllMatches()
          this.selectCurrentMatch()
          this.$message.success(`找到 ${this.matches.length} 个匹配项`)
        } else {
          this.$message.info('未找到匹配内容')
        }
      } catch (error) {
        this.$message.error('搜索失败: ' + error.message)
      }
    },
    
    getEditorText(editor) {
      try {
        if (editor.state && editor.state.doc) {
          return editor.state.doc.textContent
        }
        return ''
      } catch (error) {
        return ''
      }
    },
    
    findMatches(text) {
      this.matches = []
      const flags = this.caseSensitive ? 'g' : 'gi'
      const regex = new RegExp(this.escapeRegExp(this.searchText), flags)
      let match
      
      while ((match = regex.exec(text)) !== null) {
        this.matches.push({
          start: match.index,
          end: match.index + match[0].length,
          text: match[0]
        })
      }
      return this.matches
    },
    
    highlightAllMatches() {
      if (this.isHighlighting) {
        return
      }
      this.isHighlighting = true
      this.clearHighlight()
      
      if (!this.editorInstance || !this.editorInstance.view || !this.editorInstance.view.dom) {
        this.isHighlighting = false
        return
      }
      try {
        this.highlightTextInElement(this.editorInstance.view.dom)
        this.retryCount = 0
      } catch (error) {
      } finally {
        this.isHighlighting = false
      }
    },
    
    highlightTextInElement(element) {
      const walker = document.createTreeWalker(
        element,
        NodeFilter.SHOW_TEXT,
        null,
        false
      )
      
      const textNodes = []
      let node
      while (node = walker.nextNode()) {
        textNodes.push(node)
      }
      
      for (let i = 0; i < textNodes.length; i++) {
        this.highlightInTextNode(textNodes[i])
      }
    },
    
    highlightInTextNode(textNode) {
      const text = textNode.textContent
      const flags = this.caseSensitive ? 'g' : 'gi'
      const regex = new RegExp(this.escapeRegExp(this.searchText), flags)
      
      const localMatches = []
      let match
      while ((match = regex.exec(text)) !== null) {
        localMatches.push({
          start: match.index,
          end: match.index + match[0].length,
          text: match[0]
        })
      }
      
      if (localMatches.length === 0) return
      
      const parent = textNode.parentNode
      const fragment = document.createDocumentFragment()
      let lastIndex = 0
      
      localMatches.forEach((match) => {
        if (match.start > lastIndex) {
          fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.start)))
        }
        
        const span = document.createElement('span')
        span.textContent = match.text
        span.className = 'search-highlight'
        span.style.backgroundColor = 'rgba(255, 255, 0, 0.6)'
        span.style.borderRadius = '2px'
        span.style.padding = '1px 2px'
        span.setAttribute('data-search-match', 'true')
        
        const globalIndex = this.highlightedElements.length
        span.setAttribute('data-global-index', globalIndex.toString())
        
        fragment.appendChild(span)
        this.highlightedElements.push(span)
        lastIndex = match.end
      })
      
      if (lastIndex < text.length) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex)))
      }
      
      parent.replaceChild(fragment, textNode)
    },
    
    selectCurrentMatch() {
      if (!this.hasMatches || this.highlightedElements.length === 0) {
        return
      }
      
      if (this.currentMatchIndex < 0 || this.currentMatchIndex >= this.highlightedElements.length) {
        if (this.highlightedElements.length > 0) {
          this.currentMatchIndex = Math.min(this.currentMatchIndex, this.highlightedElements.length - 1)
          this.currentMatchIndex = Math.max(this.currentMatchIndex, 0)
        } else {
          return
        }
      }
      
      const validElements = []
      this.highlightedElements.forEach((element) => {
        if (element && element.parentNode && document.contains(element)) {
          validElements.push(element)
        }
      })
      
      if (validElements.length !== this.highlightedElements.length) {
        setTimeout(() => {
          this.restoreHighlights()
        }, 200)
        return
      }
      
      if (this.domObserver) {
        this.domObserver.disconnect()
      }
      
      this.highlightedElements.forEach((element, index) => {
        if (!element || !element.parentNode) {
          return
        }
        this.applyElementStyle(element, index === this.currentMatchIndex)
      })
      
      setTimeout(() => {
        this.setupEditorListeners()
      }, 100)
      
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },
    
    applyElementStyle(element, isCurrent) {
      if (!element) return
      
      element.classList.remove('search-highlight-current', 'search-highlight-normal')
      element.removeAttribute('style')
      element.offsetHeight
      
      if (isCurrent) {
        const currentStyles = [
          'background-color: rgba(255, 165, 0, 0.95) !important',
          'box-shadow: 0 0 10px rgba(255, 165, 0, 1) !important',
          'border: 3px solid rgba(255, 165, 0, 1) !important',
          'border-radius: 4px !important',
          'padding: 3px 6px !important',
          'font-weight: bold !important',
          'transform: scale(1.05) !important',
          'transition: all 0.2s ease !important',
          'z-index: 9999 !important',
          'position: relative !important',
          'display: inline-block !important'
        ]
        
        element.style.cssText = currentStyles.join('; ')
        element.classList.add('search-highlight-current')
        
        currentStyles.forEach(style => {
          const [property, value] = style.split(':')
          if (property && value) {
            element.style.setProperty(property.trim(), value.replace('!important', '').trim(), 'important')
          }
        })
        
        setTimeout(() => {
          try {
            element.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'nearest'
            })
          } catch (scrollError) {
          }
        }, 100)
      } else {
        const normalStyles = [
          'background-color: rgba(255, 255, 0, 0.6) !important',
          'box-shadow: none !important',
          'border: 1px solid rgba(255, 255, 0, 0.8) !important',
          'border-radius: 2px !important',
          'padding: 1px 2px !important',
          'font-weight: normal !important',
          'transform: none !important',
          'transition: all 0.2s ease !important',
          'z-index: 1 !important',
          'position: relative !important',
          'display: inline-block !important'
        ]
        
        element.style.cssText = normalStyles.join('; ')
        element.classList.add('search-highlight-normal')
        
        normalStyles.forEach(style => {
          const [property, value] = style.split(':')
          if (property && value) {
            element.style.setProperty(property.trim(), value.replace('!important', '').trim(), 'important')
          }
        })
      }
      
      element.offsetHeight
      element.offsetWidth
    },
    
    nextMatch() {
      if (!this.hasMatches) {
        return
      }
      this.currentMatchIndex = (this.currentMatchIndex + 1) % this.matches.length
      this.selectCurrentMatch()
    },
    
    previousMatch() {
      if (!this.hasMatches) {
        return
      }
      this.currentMatchIndex = (this.currentMatchIndex - 1 + this.matches.length) % this.matches.length
      this.selectCurrentMatch()
    },
    
    replace() {
      if (!this.hasMatches || !this.replaceText) {
        this.$message.warning('请输入替换内容')
        return
      }
      
      if (!this.editorInstance || !this.editorInstance.view) {
        this.$message.error('编辑器不可用')
        return
      }
      
      try {
        this.cleanupEditorListeners()
        const editorDom = this.editorInstance.view.dom
        
        const selectors = [
          '.search-highlight-current',
          '.search-highlight-normal', 
          '.search-highlight',
          'span[class*="search-highlight"]',
          '[data-search-match="true"]',
          'span[style*="background-color"]'
        ]
        
        let actualCurrentElement = null
        
        selectors.forEach(selector => {
          const elements = editorDom.querySelectorAll(selector)
          elements.forEach(el => {
            if (el.classList.contains('search-highlight-current') || 
                (el.style && el.style.backgroundColor && el.style.backgroundColor.includes('255, 165, 0'))) {
              actualCurrentElement = el
            }
          })
        })
        
        if (!actualCurrentElement) {
          if (this.highlightedElements.length > 0 && this.currentMatchIndex < this.highlightedElements.length) {
            const arrayElement = this.highlightedElements[this.currentMatchIndex]
            if (arrayElement && arrayElement.parentNode && document.contains(arrayElement)) {
              actualCurrentElement = arrayElement
            }
          }
          
          if (!actualCurrentElement) {
            this.$message.error('找不到当前选中的项目，可能需要重新搜索')
            return
          }
        }
        
        if (!document.contains(actualCurrentElement) || !actualCurrentElement.parentNode) {
          this.$message.error('当前选中的元素已失效，请重新选择')
          return
        }
        
        const success = this.replaceHighlightElement(actualCurrentElement, this.replaceText)
        
        if (success) {
          this.$message.success(`替换成功: "${actualCurrentElement.textContent}" → "${this.replaceText}"`)
          this.refreshSearchAfterReplace()
        } else {
          this.$message.error('替换失败')
        }
      } catch (error) {
        this.$message.error('替换失败: ' + error.message)
      }
    },
    
    replaceAll() {
      if (!this.hasMatches || !this.replaceText) {
        this.$message.warning('请输入替换内容')
        return
      }
      
      const totalCount = this.matches.length
      if (totalCount === 0) {
        this.$message.warning('没有找到匹配项')
        return
      }
      
      this.$confirm(`确定要替换全部 ${totalCount} 处 "${this.searchText}" 为 "${this.replaceText}" 吗？`, '确认替换', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.executeReplaceAll(totalCount)
      }).catch(() => {
      })
    },
    
    executeReplaceAll(totalCount) {
      if (!this.editorInstance || !this.editorInstance.view) {
        this.$message.error('编辑器不可用')
        return
      }
      
      try {
        this.cleanupEditorListeners()
        const docText = this.editorInstance.view.state.doc.textContent
        const escapedSearchText = this.escapeRegExp(this.searchText)
        const regex = new RegExp(escapedSearchText, this.caseSensitive ? 'g' : 'gi')
        const originalMatches = docText.match(regex) || []
        const successCount = originalMatches.length
        const success = this.performProseMirrorReplace(0, docText.length, this.replaceText)
        
        if (success) {
          this.clearSearch()
          this.$message.success(`已成功替换 ${successCount} 处内容`)
        } else {
          this.$message.error('替换失败')
        }
      } catch (error) {
        this.$message.error('替换过程中出现错误: ' + error.message)
      }
    },
    
    performProseMirrorReplace(startPos, endPos, replaceText) {
      if (!this.editorInstance || !this.editorInstance.view) {
        return false
      }
      
      try {
        this.clearHighlight()
        
        let docHTML
        if (this.editorInstance.getHTML) {
          docHTML = this.editorInstance.getHTML()
        } else if (this.editorInstance.view.dom) {
          docHTML = this.editorInstance.view.dom.innerHTML
        } else {
          return false
        }
        
        const docText = this.editorInstance.view.state.doc.textContent
        let newDocHTML
        
        if (startPos === 0 && endPos === docText.length) {
          const searchTextEscaped = this.escapeRegExp(this.searchText)
          const regex = new RegExp(searchTextEscaped, this.caseSensitive ? 'g' : 'gi')
          newDocHTML = docHTML.replace(regex, replaceText)
        } else {
          if (startPos < 0 || endPos > docText.length || startPos >= endPos) {
            return false
          }
          
          const targetText = docText.substring(startPos, endPos)
          const escapedTargetText = this.escapeRegExp(targetText)
          const regex = new RegExp(escapedTargetText, this.caseSensitive ? '' : 'i')
          newDocHTML = docHTML.replace(regex, replaceText)
        }
        
        try {
          if (this.editorInstance.commands && this.editorInstance.commands.setContent) {
            this.editorInstance.commands.setContent(newDocHTML, false)
            return true
          } else if (this.editorInstance.chain && this.editorInstance.chain().setContent) {
            this.editorInstance.chain().setContent(newDocHTML).run()
            return true
          } else if (this.editorInstance.setContent) {
            this.editorInstance.setContent(newDocHTML, false)
            return true
          } else if (this.$parent && this.$parent.$refs.editor) {
            this.$parent.$refs.editor.value = newDocHTML
            return true
          } else {
            return false
          }
        } catch (error) {
          return false
        }
      } catch (error) {
        return false
      }
    },
    
    replaceHighlightElement(highlightElement, replaceText) {
      if (!highlightElement || !highlightElement.parentNode) {
        return false
      }
      
      try {
        const textNode = document.createTextNode(replaceText)
        highlightElement.parentNode.replaceChild(textNode, highlightElement)
        return true
      } catch (error) {
        return false
      }
    },
    
    refreshSearchAfterReplace() {
      setTimeout(() => {
        const currentSearchText = this.searchText
        if (currentSearchText && currentSearchText.trim()) {
          const oldIndex = this.currentMatchIndex
          this.search()
          
          if (this.hasMatches) {
            const newIndex = Math.min(oldIndex, this.matches.length - 1)
            if (newIndex >= 0) {
              this.currentMatchIndex = newIndex
              this.selectCurrentMatch()
            }
          }
        }
      }, 150)
    },
    
    forceRemoveAllHighlights() {
      if (!this.editorInstance || !this.editorInstance.view || !this.editorInstance.view.dom) {
        return
      }
      
      try {
        const editorDom = this.editorInstance.view.dom
        const highlightSelectors = [
          'span.search-highlight',
          'span.search-highlight-current', 
          'span.search-highlight-normal',
          '[data-search-match="true"]',
          'span[class*="search-highlight"]',
          'span[style*="background-color: rgba(255, 255, 0"]',
          'span[style*="background-color: rgba(255, 165, 0"]'
        ]
        
        highlightSelectors.forEach((selector) => {
          const elements = editorDom.querySelectorAll(selector)
          for (let i = elements.length - 1; i >= 0; i--) {
            const element = elements[i]
            if (element && element.parentNode) {
              try {
                const textContent = element.textContent || ''
                const textNode = document.createTextNode(textContent)
                element.parentNode.replaceChild(textNode, element)
              } catch (error) {
              }
            }
          }
        })
        
        let cleanHTML = editorDom.innerHTML
        const htmlPatterns = [
          /<span[^>]*class="[^"]*search-highlight[^"]*"[^>]*>(.*?)<\/span>/gi,
          /<span[^>]*data-search-match="true"[^>]*>(.*?)<\/span>/gi,
          /<span[^>]*style="[^"]*background-color:\s*rgba\(255,\s*255,\s*0[^"]*"[^>]*>(.*?)<\/span>/gi,
          /<span[^>]*style="[^"]*background-color:\s*rgba\(255,\s*165,\s*0[^"]*"[^>]*>(.*?)<\/span>/gi
        ]
        
        let htmlCleaned = false
        htmlPatterns.forEach((pattern) => {
          const beforeLength = cleanHTML.length
          cleanHTML = cleanHTML.replace(pattern, '$1')
          if (cleanHTML.length !== beforeLength) {
            htmlCleaned = true
          }
        })
        
        if (htmlCleaned) {
          editorDom.innerHTML = cleanHTML
        }
        
        this.highlightedElements = []
        this.matches = []
        this.currentMatchIndex = 0
      } catch (error) {
      }
    },
    
    clearHighlight() {
      if (this.highlightedElements.length === 0) {
        return
      }
      
      const shouldManageListeners = !this.isHighlighting
      
      if (shouldManageListeners) {
        this.isHighlighting = true
        if (this.domObserver) {
          this.domObserver.disconnect()
        }
      }
      
      try {
        for (let i = this.highlightedElements.length - 1; i >= 0; i--) {
          const element = this.highlightedElements[i]
          
          if (!element || !element.parentNode || !document.contains(element)) {
            continue
          }
          
          try {
            const textNode = document.createTextNode(element.textContent)
            element.parentNode.replaceChild(textNode, element)
          } catch (elementError) {
          }
        }
      } catch (error) {
      } finally {
        this.highlightedElements = []
        
        if (shouldManageListeners) {
          setTimeout(() => {
            this.setupEditorListeners()
          }, 100)
        }
      }
    },
    
    clearSearch() {
      this.isHighlighting = false
      this.retryCount = 0
      this.clearHighlight()
      this.matches = []
      this.currentMatchIndex = 0
      this.highlightedElements = []
    },
    
    handleClose() {
      this.clearSearch()
      this.$emit('update:visible', false)
    },
    
    escapeRegExp(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    },
    
    searchSpaces() {
      this.forceRemoveAllHighlights()
      this.clearSearch()
      this.searchText = ' '
      
      this.$nextTick(() => {
        if (!this.editorInstance || !this.editorInstance.view) {
          this.$message.error('编辑器不可用')
          return
        }
        
        try {
          const text = this.getEditorText(this.editorInstance)
          this.isHighlighting = false
          this.retryCount = 0
          this.matches = []
          const spaceRegex = / /g
          let match
          
          while ((match = spaceRegex.exec(text)) !== null) {
            this.matches.push({
              start: match.index,
              end: match.index + 1,
              text: ' '
            })
          }
          
          if (this.hasMatches) {
            this.currentMatchIndex = 0
            this.highlightAllMatches()
            this.selectCurrentMatch()
            this.$message.success(`找到 ${this.matches.length} 个空格`)
          } else {
            this.$message.info('未找到空格')
          }
        } catch (error) {
          this.$message.error('搜索空格失败: ' + error.message)
        }
      })
    },
    
    clearAllSpaces() {
      if (!this.editorInstance || !this.editorInstance.view) {
        this.$message.error('编辑器不可用')
        return
      }
      
      const text = this.getEditorText(this.editorInstance)
      const spaceCount = (text.match(/\s/g) || []).length
      
      if (spaceCount === 0) {
        this.$message.info('文档中没有空格')
        return
      }
      
      this.$confirm(`确定要清除文档中的全部 ${spaceCount} 个空格吗？`, '确认清除空格', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.executeClearAllSpaces(spaceCount)
      }).catch(() => {
      })
    },
    
    executeClearAllSpaces(spaceCount) {
      if (!this.editorInstance || !this.editorInstance.view) {
        this.$message.error('编辑器不可用')
        return
      }
      
      try {
        this.cleanupEditorListeners()
        
        let docHTML
        if (this.editorInstance.getHTML) {
          docHTML = this.editorInstance.getHTML()
        } else if (this.editorInstance.view.dom) {
          docHTML = this.editorInstance.view.dom.innerHTML
        } else {
          this.$message.error('无法获取编辑器内容')
          return
        }
        
        const parser = new DOMParser()
        const doc = parser.parseFromString(docHTML, 'text/html')
        const walker = document.createTreeWalker(
          doc.body,
          NodeFilter.SHOW_TEXT,
          null,
          false
        )
        
        let node
        while (node = walker.nextNode()) {
          node.textContent = node.textContent.replace(/\s/g, '')
        }
        
        const finalHTML = doc.body.innerHTML
        
        try {
          if (this.editorInstance.commands && this.editorInstance.commands.setContent) {
            this.editorInstance.commands.setContent(finalHTML, false)
          } else if (this.editorInstance.chain && this.editorInstance.chain().setContent) {
            this.editorInstance.chain().setContent(finalHTML).run()
          } else if (this.editorInstance.setContent) {
            this.editorInstance.setContent(finalHTML, false)
          } else {
            this.$message.error('无法更新编辑器内容')
            return
          }
          
          this.clearSearch()
          this.$message.success(`已成功清除 ${spaceCount} 个空格`)
        } catch (error) {
          this.$message.error('更新编辑器内容失败')
        }
      } catch (error) {
        this.$message.error('清除空格过程中出现错误: ' + error.message)
      }
    },
    
    deleteCurrent() {
      if (!this.hasMatches) {
        this.$message.warning('没有找到匹配项')
        return
      }
      
      if (!this.editorInstance || !this.editorInstance.view) {
        this.$message.error('编辑器不可用')
        return
      }
      
      try {
        this.cleanupEditorListeners()
        const editorDom = this.editorInstance.view.dom
        
        const selectors = [
          '.search-highlight-current',
          '.search-highlight-normal', 
          '.search-highlight',
          'span[class*="search-highlight"]',
          '[data-search-match="true"]',
          'span[style*="background-color"]'
        ]
        
        let actualCurrentElement = null
        
        selectors.forEach(selector => {
          const elements = editorDom.querySelectorAll(selector)
          elements.forEach(el => {
            if (el.classList.contains('search-highlight-current') || 
                (el.style && el.style.backgroundColor && el.style.backgroundColor.includes('255, 165, 0'))) {
              actualCurrentElement = el
            }
          })
        })
        
        if (!actualCurrentElement) {
          if (this.highlightedElements.length > 0 && this.currentMatchIndex < this.highlightedElements.length) {
            const arrayElement = this.highlightedElements[this.currentMatchIndex]
            if (arrayElement && arrayElement.parentNode && document.contains(arrayElement)) {
              actualCurrentElement = arrayElement
            }
          }
          
          if (!actualCurrentElement) {
            this.$message.error('找不到当前选中的项目，可能需要重新搜索')
            return
          }
        }
        
        if (!document.contains(actualCurrentElement) || !actualCurrentElement.parentNode) {
          this.$message.error('当前选中的元素已失效，请重新选择')
          return
        }
        
        const deletedText = actualCurrentElement.textContent
        const success = this.deleteHighlightElement(actualCurrentElement)
        
        if (success) {
          this.$message.success(`已消除: "${deletedText}"`)
          this.refreshSearchAfterReplace()
        } else {
          this.$message.error('消除失败')
        }
      } catch (error) {
        this.$message.error('消除失败: ' + error.message)
      }
    },
    
    deleteAll() {
      if (!this.hasMatches) {
        this.$message.warning('没有找到匹配项')
        return
      }
      
      const totalCount = this.matches.length
      if (totalCount === 0) {
        this.$message.warning('没有找到匹配项')
        return
      }
      
      this.$confirm(`确定要消除全部 ${totalCount} 处 "${this.searchText}" 吗？`, '确认消除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.executeDeleteAll(totalCount)
      }).catch(() => {
      })
    },
    
    executeDeleteAll(totalCount) {
      if (!this.editorInstance || !this.editorInstance.view) {
        this.$message.error('编辑器不可用')
        return
      }
      
      try {
        this.cleanupEditorListeners()
        const docText = this.editorInstance.view.state.doc.textContent
        const escapedSearchText = this.escapeRegExp(this.searchText)
        const regex = new RegExp(escapedSearchText, this.caseSensitive ? 'g' : 'gi')
        const originalMatches = docText.match(regex) || []
        const successCount = originalMatches.length
        
        // 使用空字符串替换，实现删除效果
        const success = this.performProseMirrorReplace(0, docText.length, '')
        
        if (success) {
          this.clearSearch()
          this.$message.success(`已成功消除 ${successCount} 处内容`)
        } else {
          this.$message.error('消除失败')
        }
      } catch (error) {
        this.$message.error('消除过程中出现错误: ' + error.message)
      }
    },
    
    deleteHighlightElement(highlightElement) {
      if (!highlightElement || !highlightElement.parentNode) {
        return false
      }
      
      try {
        // 直接删除元素，不替换为任何内容
        highlightElement.parentNode.removeChild(highlightElement)
        return true
      } catch (error) {
        return false
      }
    },
    
    setupEditorListeners() {
    },
    
    cleanupEditorListeners() {
      if (this.editorInstance && this.editorUpdateHandler) {
        this.editorInstance.off('update', this.editorUpdateHandler)
        this.editorInstance.off('transaction', this.editorUpdateHandler)
      }
      
      if (this.domObserver) {
        this.domObserver.disconnect()
      }
    },
    
    checkAndRestoreHighlights() {
      if (this.isHighlighting || this.matches.length === 0) return
      
      const validElements = this.highlightedElements.filter(element => 
        element && element.parentNode && document.contains(element)
      )
      
      if (validElements.length !== this.highlightedElements.length) {
        this.restoreHighlights()
      }
    },
    
    restoreHighlights() {
      if (this.retryCount > 3) {
        return
      }
      
      this.retryCount++
      
      setTimeout(() => {
        this.highlightAllMatches()
        this.selectCurrentMatch()
      }, 100)
    }
  },
  
  mounted() {
    this.setupEditorListeners()
  },
  
  beforeDestroy() {
    this.cleanupEditorListeners()
    this.clearHighlight()
  }
}
</script>

<style lang="scss" scoped>
.search-panel {
  position: absolute;
  top: 50px;
  right: 20px;
  width: 320px;
  background: white;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  z-index: 1000;
  padding: 0;
}

.search-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #ebeef5;
  background: #f5f7fa;
}

.search-title {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}

.search-close {
  cursor: pointer;
  color: #909399;
  font-size: 16px;
  
  &:hover {
    color: #606266;
  }
}

.search-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
}

.search-row {
  display: flex;
  align-items: center;
}

.replace-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.delete-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid #ebeef5;
}

.space-buttons {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid #ebeef5;
}

.search-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.match-info {
  font-size: 12px;
  color: #606266;
}

.search-navigation {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 5px;
  }
</style>

<style>
.search-highlight {
  display: inline !important;
  background-color: rgba(255, 255, 0, 0.6) !important;
  border-radius: 2px !important;
  padding: 1px 2px !important;
}

.search-highlight-current {
  display: inline !important;
  background-color: rgba(255, 165, 0, 0.9) !important;
  box-shadow: 0 0 8px rgba(255, 165, 0, 0.8) !important;
  border: 2px solid rgba(255, 165, 0, 1) !important;
  border-radius: 3px !important;
  padding: 2px 4px !important;
  font-weight: bold !important;
  transition: all 0.3s ease !important;
  z-index: 999 !important;
  position: relative !important;
}

.search-highlight-normal {
  display: inline !important;
  background-color: rgba(255, 255, 0, 0.5) !important;
  box-shadow: none !important;
  border: 1px solid rgba(255, 255, 0, 0.8) !important;
  border-radius: 2px !important;
  padding: 1px 2px !important;
  font-weight: normal !important;
  transition: all 0.3s ease !important;
  z-index: 1 !important;
  position: relative !important;
}

.search-highlight-current,
.search-highlight-normal {
  color: inherit !important;
  text-decoration: none !important;
}
</style>